import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { Plus, Edit, Key, Trash2, Shield, Briefcase, FileText, Check, Minus, UserCog, Sliders, Users, UserCheck, Link2 } from 'lucide-react';
import UserForm, { User } from '../components/UserForm';
import RoleChangeModal, { RoleChangeData } from '../components/RoleChangeModal';
import ResetPasswordForm from '../components/ResetPasswordForm';
import DataTable from '../components/DataTable';
import IconButton from '../components/common/IconButton';
import SmartSearchBar from '../components/leads/SmartSearchBar';
import { partialSupervisorsApi } from '../services/partialSupervisorService';
import PartialSupervisorManagementModal from '../components/partialSupervisor/PartialSupervisorManagementModal';
import SalesRepSupervisorsManagementModal from '../components/partialSupervisor/SalesRepSupervisorsManagementModal';
import SecretaryAssignmentsModal from '../components/secretary/SecretaryAssignmentsModal';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--surface-light, #f3f4f6);
  padding: 4px 12px;
  border-radius: 16px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FilterContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: var(--shadow-light);
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 0;
  flex-wrap: wrap;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  ${props => props.$isActive ? `
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
  ` : `
    background: white;
    color: var(--text-secondary);
    border-color: var(--border-color);

    &:hover {
      border-color: var(--primary-color);
      color: var(--primary-color);
    }
  `}
`;

const StatusSelect = styled.select`
  padding: 10px 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  background: white;
  cursor: pointer;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const PermissionText = styled.span<{ level: number }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  filter: saturate(0.7) brightness(0.85);

  ${props => {
    switch (props.level) {
      case 1:
        return `color: #dc2626;`;
      case 2:
        return `color: #d97706;`;
      case 3:
        return `color: #2563eb;`;
      case 4:
        return `color: #7c3aed;`;
      default:
        return `color: #6b7280;`;
    }
  }}
`;

const StatusText = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.$active ? '#16a34a' : '#dc2626'};
  filter: saturate(0.7) brightness(0.85);
`;

const PartialBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const PartialLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #16a34a;
  background: rgba(22, 163, 74, 0.1);
  padding: 1px 6px;
  border-radius: 4px;
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

const CenteredCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const TextCell = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const SecondaryText = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid var(--error-color);
`;

const SuccessMessage = styled.div`
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid rgba(22, 163, 74, 0.3);
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ConfirmModal = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  padding: 24px;
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

const ConfirmTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const ConfirmText = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: var(--text-secondary);
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

interface UserResponse {
  id: number;
  fullName: string;
  email: string;
  phone: string;
  permissionLevel: number;
  permissionLevelName: string;
  isSupervisor: boolean;
  isPartialSupervisor: boolean;
  isActive: boolean;
  defaultCommissionFixedAmount: number;
  defaultCommissionPercentage: number;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
}

const UsersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [isResetPasswordFormOpen, setIsResetPasswordFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [secretaryForAssign, setSecretaryForAssign] = useState<UserResponse | null>(null);

  // Filters - read from URL
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [permissionFilter, setPermissionFilter] = useState<string[]>(() => {
    const permissions = searchParams.get('permissions');
    return permissions ? permissions.split(',') : [];
  });
  const [statusFilter, setStatusFilter] = useState<string>(() => searchParams.get('status') || '');

  // Delete confirmation
  const [userToDelete, setUserToDelete] = useState<UserResponse | null>(null);

  // Role change confirmation
  const [roleChangeData, setRoleChangeData] = useState<RoleChangeData | null>(null);

  // Partial supervisor — manage assigned sales reps
  const [managingSupervisor, setManagingSupervisor] = useState<UserResponse | null>(null);
  // Sales rep — manage the partial supervisors responsible for them
  const [managingRepSupervisors, setManagingRepSupervisors] = useState<UserResponse | null>(null);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (searchTerm) params.set('search', searchTerm);
    if (permissionFilter.length > 0) params.set('permissions', permissionFilter.join(','));
    if (statusFilter) params.set('status', statusFilter);

    setSearchParams(params, { replace: true });
  }, [searchTerm, permissionFilter, statusFilter, setSearchParams]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage({ type: 'error', text: 'שגיאה בטעינת המשתמשים' });
    } finally {
      setLoading(false);
    }
  };

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          user.fullName.toLowerCase().includes(search) ||
          user.email.toLowerCase().includes(search) ||
          user.phone.includes(search);
        if (!matchesSearch) return false;
      }

      // Permission filter (multi-select)
      if (permissionFilter.length > 0 && !permissionFilter.includes(user.permissionLevel.toString())) {
        return false;
      }

      // Status filter
      if (statusFilter === 'active' && !user.isActive) return false;
      if (statusFilter === 'inactive' && user.isActive) return false;

      return true;
    });
  }, [users, searchTerm, permissionFilter, statusFilter]);

  // Toggle permission filter (multi-select)
  const handlePermissionToggle = (level: string) => {
    if (level === '') {
      // "All" button - clear selection
      setPermissionFilter([]);
    } else {
      // Toggle individual permission level
      if (permissionFilter.includes(level)) {
        setPermissionFilter(permissionFilter.filter(p => p !== level));
      } else {
        setPermissionFilter([...permissionFilter, level]);
      }
    }
  };

  const handleCreateUser = async (user: User, password?: string, salesRepIds?: number[], groupName?: string) => {
    try {
      setIsSubmitting(true);
      setMessage(null);

      if (user.isPartialSupervisor) {
        // מפקח חלקי — יצירה דרך ה-endpoint הייעודי (נציג מכירות + קבוצה + נציגים)
        await partialSupervisorsApi.create({
          fullName: user.fullName,
          email: user.email,
          phone: user.phone,
          password: password || '',
          groupName: groupName,
          salesRepIds: salesRepIds || []
        });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/users`, {
          fullName: user.fullName,
          email: user.email,
          phone: user.phone,
          permissionLevel: user.permissionLevel,
          isSupervisor: user.isSupervisor,
          isPartialSupervisor: false,
          defaultCommissionFixedAmount: user.defaultCommissionFixedAmount,
          defaultCommissionPercentage: user.defaultCommissionPercentage,
          password
        });
      }

      setMessage({ type: 'success', text: 'המשתמש נוסף בהצלחה' });
      await fetchUsers();
      setIsUserFormOpen(false);
    } catch (error: any) {
      console.error('Error creating user:', error);
      let errorMessage = 'שגיאה ביצירת המשתמש';

      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages: string[] = [];
        Object.values(validationErrors).forEach((value: any) => {
          if (Array.isArray(value)) {
            errorMessages.push(...value);
          } else {
            errorMessages.push(String(value));
          }
        });
        errorMessage = errorMessages.join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data) {
        errorMessage = typeof error.response.data === 'string'
          ? error.response.data
          : JSON.stringify(error.response.data);
      }

      setMessage({ type: 'error', text: errorMessage });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async (user: User) => {
    if (!selectedUser) return;

    const roleChanged = user.permissionLevel !== selectedUser.permissionLevel;

    try {
      setIsSubmitting(true);
      setMessage(null);
      // עדכון פרטי הפרופיל. השרת מתעלם מ-permissionLevel — שינוי תפקיד עובר דרך הזרימה הייעודית.
      await axios.put(`${process.env.REACT_APP_API_URL}/users/${selectedUser.id}`, {
        fullName: user.fullName,
        email: user.email,
        phone: user.phone,
        permissionLevel: user.permissionLevel,
        isSupervisor: user.isSupervisor,
        isPartialSupervisor: user.isPartialSupervisor,
        isActive: user.isActive,
        defaultCommissionFixedAmount: user.defaultCommissionFixedAmount,
        defaultCommissionPercentage: user.defaultCommissionPercentage
      });

      await fetchUsers();
      setIsUserFormOpen(false);

      if (roleChanged) {
        // פתיחת חלון אישור שינוי תפקיד עם פירוט ההשפעה והבחירות
        setRoleChangeData({
          userId: selectedUser.id,
          userName: user.fullName,
          oldLevel: selectedUser.permissionLevel,
          newLevel: user.permissionLevel,
          isSupervisor: user.isSupervisor,
        });
      } else {
        setMessage({ type: 'success', text: 'המשתמש עודכן בהצלחה' });
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      let errorMessage = 'שגיאה בעדכון המשתמש';

      if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages: string[] = [];
        Object.values(validationErrors).forEach((value: any) => {
          if (Array.isArray(value)) {
            errorMessages.push(...value);
          } else {
            errorMessages.push(String(value));
          }
        });
        errorMessage = errorMessages.join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setMessage({ type: 'error', text: errorMessage });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (newPassword: string) => {
    if (!selectedUser) return;

    try {
      setIsSubmitting(true);
      await axios.post(`${process.env.REACT_APP_API_URL}/users/${selectedUser.id}/reset-password`, {
        newPassword
      });

      setMessage({ type: 'success', text: 'הסיסמה עודכנה בהצלחה' });
      setIsResetPasswordFormOpen(false);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      const errorMessage = error.response?.data?.message || 'שגיאה באיפוס הסיסמה';
      setMessage({ type: 'error', text: errorMessage });
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/users/${userToDelete.id}`);
      const successText = response.data?.message
        || (response.data?.wasDeactivated ? 'המזכירה הושבתה' : 'המשתמש נמחק בהצלחה');
      setMessage({ type: 'success', text: successText });
      await fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.message || 'שגיאה במחיקת המשתמש';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setUserToDelete(null);
    }
  };

  const openEditForm = (user: UserResponse) => {
    setSelectedUser(user);
    setIsUserFormOpen(true);
  };

  const openResetPasswordForm = (user: UserResponse) => {
    setSelectedUser(user);
    setIsResetPasswordFormOpen(true);
  };

  const closeForm = () => {
    setSelectedUser(null);
    setIsUserFormOpen(false);
    setIsResetPasswordFormOpen(false);
  };

  const handleRoleChangeSuccess = async (msg: string) => {
    setRoleChangeData(null);
    setSelectedUser(null);
    setMessage({ type: 'success', text: msg });
    await fetchUsers();
  };

  const handleRoleChangeClose = () => {
    setRoleChangeData(null);
    setSelectedUser(null);
    setMessage({ type: 'success', text: 'פרטי המשתמש עודכנו. התפקיד לא שונה.' });
  };

  const getPermissionLevelName = (level: number) => {
    switch (level) {
      case 1: return 'מנהל מערכת';
      case 2: return 'נציג מכירות';
      case 3: return 'מנהל תיקים';
      case 4: return 'מזכירה';
      case 5: return 'מנהל הגדרות';
      default: return 'לא ידוע';
    }
  };

  const getPermissionIcon = (level: number) => {
    switch (level) {
      case 1: return Shield;
      case 2: return Briefcase;
      case 3: return FileText;
      case 4: return UserCog;
      case 5: return Sliders;
      default: return Shield;
    }
  };

  const formatLastLogin = (dateString: string | null): string => {
    if (!dateString) return 'טרם התחבר';
    const date = new Date(dateString);
    return date.toLocaleString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const columns = [
    {
      key: 'fullName',
      title: 'שם',
      width: '150px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          <TextCell>{record.fullName}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'email',
      title: 'מייל',
      width: '200px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          <TextCell>{record.email}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'phone',
      title: 'טלפון',
      width: '120px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          <TextCell>{record.phone}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'permissionLevel',
      title: 'רמת הרשאה',
      width: '140px',
      render: (_: any, record: UserResponse) => {
        const Icon = getPermissionIcon(record.permissionLevel);
        return (
          <CenteredCell>
            <PermissionText level={record.permissionLevel}>
              <Icon size={14} />
              {getPermissionLevelName(record.permissionLevel)}
            </PermissionText>
          </CenteredCell>
        );
      }
    },
    {
      key: 'isSupervisor',
      title: 'מפקח',
      width: '110px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          {record.isPartialSupervisor ? (
            <PartialBadge>
              <Check size={14} color="#16a34a" strokeWidth={2.5} />
              <PartialLabel>חלקי</PartialLabel>
            </PartialBadge>
          ) : record.isSupervisor ? (
            <Check size={14} color="#16a34a" strokeWidth={2.5} />
          ) : (
            <Minus size={14} color="#d1d5db" />
          )}
        </CenteredCell>
      )
    },
    {
      key: 'lastLoginAt',
      title: 'התחברות אחרונה',
      width: '160px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          <SecondaryText>{formatLastLogin(record.lastLoginAt)}</SecondaryText>
        </CenteredCell>
      )
    },
    {
      key: 'isActive',
      title: 'סטטוס',
      width: '100px',
      render: (_: any, record: UserResponse) => (
        <CenteredCell>
          <StatusText $active={record.isActive}>
            {record.isActive ? 'פעיל' : 'לא פעיל'}
          </StatusText>
        </CenteredCell>
      )
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '170px',
      render: (_: any, record: UserResponse) => (
        <ActionsCell>
          {record.permissionLevel === 4 && (
            <IconButton
              icon={Link2}
              onClick={(e) => { e.stopPropagation(); setSecretaryForAssign(record); }}
              title="שיוך לידים ולקוחות"
            />
          )}
          {record.isPartialSupervisor && (
            <IconButton
              icon={Users}
              onClick={(e) => { e.stopPropagation(); setManagingSupervisor(record); }}
              title="ניהול נציגים"
              variant="primary"
            />
          )}
          {record.permissionLevel === 2 && !record.isSupervisor && !record.isPartialSupervisor && (
            <IconButton
              icon={UserCheck}
              onClick={(e) => { e.stopPropagation(); setManagingRepSupervisors(record); }}
              title="ניהול מפקחים"
            />
          )}
          <IconButton
            icon={Edit}
            onClick={(e) => { e.stopPropagation(); openEditForm(record); }}
            title="עריכת משתמש"
          />
          <IconButton
            icon={Key}
            onClick={(e) => { e.stopPropagation(); openResetPasswordForm(record); }}
            title="איפוס סיסמה"
            variant="primary"
          />
          <IconButton
            icon={Trash2}
            onClick={(e) => { e.stopPropagation(); setUserToDelete(record); }}
            title="מחיקת משתמש"
            variant="danger"
          />
        </ActionsCell>
      )
    }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <HeaderRight>
          <PageTitle>ניהול משתמשים</PageTitle>
          <UserCount>{users.length} משתמשים</UserCount>
        </HeaderRight>
        <HeaderActions>
          <Button variant="primary" onClick={() => setIsUserFormOpen(true)}>
            <Plus size={16} />
            משתמש חדש
          </Button>
        </HeaderActions>
      </PageHeader>

      {message && (
        message.type === 'success' ? (
          <SuccessMessage>{message.text}</SuccessMessage>
        ) : (
          <ErrorMessage>{message.text}</ErrorMessage>
        )
      )}

      <FilterContainer>
        <SmartSearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="חיפוש לפי שם, אימייל או טלפון..."
        />

        <FilterRow>
          <TabsContainer>
            <Tab
              $isActive={permissionFilter.length === 0}
              onClick={() => handlePermissionToggle('')}
            >
              הכל
            </Tab>
            <Tab
              $isActive={permissionFilter.includes('1')}
              onClick={() => handlePermissionToggle('1')}
            >
              <Shield size={14} />
              מנהלי מערכת
            </Tab>
            <Tab
              $isActive={permissionFilter.includes('2')}
              onClick={() => handlePermissionToggle('2')}
            >
              <Briefcase size={14} />
              נציגי מכירות
            </Tab>
            <Tab
              $isActive={permissionFilter.includes('3')}
              onClick={() => handlePermissionToggle('3')}
            >
              <FileText size={14} />
              מנהלי תיקים
            </Tab>
            <Tab
              $isActive={permissionFilter.includes('4')}
              onClick={() => handlePermissionToggle('4')}
            >
              <UserCog size={14} />
              מזכירות
            </Tab>
          </TabsContainer>

          <StatusSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">כל הסטטוסים</option>
            <option value="active">פעילים</option>
            <option value="inactive">לא פעילים</option>
          </StatusSelect>
        </FilterRow>
      </FilterContainer>

      <DataTable
        data={filteredUsers}
        columns={columns}
        loading={loading}
      />

      <UserForm
        user={selectedUser ? {
          id: selectedUser.id,
          fullName: selectedUser.fullName,
          email: selectedUser.email,
          phone: selectedUser.phone,
          permissionLevel: selectedUser.permissionLevel,
          isSupervisor: selectedUser.isSupervisor,
          isPartialSupervisor: selectedUser.isPartialSupervisor,
          isActive: selectedUser.isActive,
          defaultCommissionFixedAmount: selectedUser.defaultCommissionFixedAmount,
          defaultCommissionPercentage: selectedUser.defaultCommissionPercentage
        } : undefined}
        isOpen={isUserFormOpen}
        onClose={closeForm}
        onSubmit={selectedUser ? handleUpdateUser : handleCreateUser}
        isLoading={isSubmitting}
      />

      <ResetPasswordForm
        isOpen={isResetPasswordFormOpen}
        onClose={closeForm}
        onSubmit={handleResetPassword}
        userFullName={selectedUser?.fullName}
        userEmail={selectedUser?.email}
        isLoading={isSubmitting}
      />

      {/* Partial supervisor — manage assigned sales reps */}
      {managingSupervisor && (
        <PartialSupervisorManagementModal
          isOpen={!!managingSupervisor}
          onClose={() => setManagingSupervisor(null)}
          supervisorId={managingSupervisor.id}
          supervisorName={managingSupervisor.fullName}
        />
      )}

      {/* Sales rep — manage the partial supervisors responsible for them */}
      {managingRepSupervisors && (
        <SalesRepSupervisorsManagementModal
          isOpen={!!managingRepSupervisors}
          onClose={() => setManagingRepSupervisors(null)}
          salesRepId={managingRepSupervisors.id}
          salesRepName={managingRepSupervisors.fullName}
        />
      )}

      {/* Secretary — manage assigned leads & clients (secretary-centric) */}
      <SecretaryAssignmentsModal
        isOpen={!!secretaryForAssign}
        secretary={secretaryForAssign ? { id: secretaryForAssign.id, fullName: secretaryForAssign.fullName } : null}
        onClose={() => setSecretaryForAssign(null)}
        onSaved={(text) => setMessage({ type: 'success', text })}
      />

      {/* Delete Confirmation Modal */}
      {userToDelete && (
        <ConfirmOverlay onClick={() => setUserToDelete(null)}>
          <ConfirmModal onClick={(e) => e.stopPropagation()}>
            <ConfirmTitle>מחיקת משתמש</ConfirmTitle>
            <ConfirmText>
              האם אתה בטוח שברצונך למחוק את המשתמש "{userToDelete.fullName}"?
              <br />
              פעולה זו אינה ניתנת לביטול.
            </ConfirmText>
            <ConfirmButtons>
              <Button variant="danger" onClick={handleDeleteUser}>
                מחק משתמש
              </Button>
              <Button onClick={() => setUserToDelete(null)}>
                ביטול
              </Button>
            </ConfirmButtons>
          </ConfirmModal>
        </ConfirmOverlay>
      )}

      <RoleChangeModal
        data={roleChangeData}
        onClose={handleRoleChangeClose}
        onSuccess={handleRoleChangeSuccess}
      />
    </PageContainer>
  );
};

export default UsersPage;
